import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ExternalLink } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { CentificLogo } from './CentificLogo';
import { ROUTES } from '../config/routes';

export function Footer() {
  const { isAuthenticated } = useAuth();
  const currentYear = new Date().getFullYear();

  // Don't show footer for authenticated users - sidebar provides navigation
  if (isAuthenticated) {
    return null;
  }

  return (
    <footer className="bg-transparent relative z-10">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Logo and Description */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <CentificLogo width={140} height={38} />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed">
              Employee Connect Forum - Empowering teams through meaningful connect and continuous improvement.
            </p>
          </div>

          {/* Quick Links - Different for authenticated/non-authenticated */}
          <div>
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {isAuthenticated ? (
                <>
                  <li>
                    <Link 
                      to={ROUTES.Dashboard} 
                      className="text-slate-400 hover:text-pink-400 transition-colors text-sm"
                    >
                      Dashboard
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to={ROUTES.ReporteeInfo} 
                      className="text-slate-400 hover:text-pink-400 transition-colors text-sm"
                    >
                      My Team
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to={ROUTES.Purpose} 
                      className="text-slate-400 hover:text-pink-400 transition-colors text-sm"
                    >
                      About Purpose
                    </Link>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <Link 
                      to={ROUTES.Home} 
                      className="text-slate-400 hover:text-pink-400 transition-colors text-sm"
                    >
                      Home
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to={ROUTES.Purpose} 
                      className="text-slate-400 hover:text-pink-400 transition-colors text-sm"
                    >
                      Purpose
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-white font-semibold mb-4">Resources</h3>
            <ul className="space-y-2">
              <li>
                <a 
                  href="https://centific.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-pink-400 transition-colors text-sm inline-flex items-center gap-1"
                >
                  Centific Website
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold mb-4">Contact</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-pink-400 mt-0.5 flex-shrink-0" />
                <a 
                  href="mailto:feedback@centific.com" 
                  className="text-slate-400 hover:text-pink-400 transition-colors text-sm"
                >
                  feedback@centific.com
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-pink-400 mt-0.5 flex-shrink-0" />
                <span className="text-slate-400 text-sm">+1 (800) 555-0123</span>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-pink-400 mt-0.5 flex-shrink-0" />
                <span className="text-slate-400 text-sm">
                  Bellevue, WA, USA
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {currentYear} Centific. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <button 
                type="button"
                className="text-slate-500 hover:text-slate-300 transition-colors text-sm bg-transparent border-none cursor-pointer"
              >
                Privacy Policy
              </button>
              <button 
                type="button"
                className="text-slate-500 hover:text-slate-300 transition-colors text-sm bg-transparent border-none cursor-pointer"
              >
                Terms of Service
              </button>
              {isAuthenticated && (
                <button 
                  type="button"
                  className="text-slate-500 hover:text-slate-300 transition-colors text-sm bg-transparent border-none cursor-pointer"
                >
                  Cookie Settings
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
